/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import NumberFormatter from '../NumberFormatter';
import {
  getUserCurrencyContext,
  resolveCurrencyStyle,
} from '../../currency-format/UserCurrencyContextSingleton';

const INDIAN: ReadonlyArray<readonly [number, string]> = [
  [1e7, 'Cr'],
  [1e5, 'L'],
  [1e3, 'K'],
];

const INTL: ReadonlyArray<readonly [number, string]> = [
  [1e9, 'B'],
  [1e6, 'M'],
  [1e3, 'K'],
];

const round1 = (n: number): string => {
  const fixed = n.toFixed(1);
  return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed;
};

export default function createSmartHotelNumberFormatter({
  withSymbol = true,
}: { withSymbol?: boolean } = {}) {
  return new NumberFormatter({
    id: withSymbol ? 'SMART_NUMBER_HOTEL' : 'SMART_NUMBER_HOTEL_NOSYM',
    label: withSymbol
      ? 'Adaptive (user currency)'
      : 'Adaptive (user currency, no symbol)',
    formatFunc: value => {
      const ctx = getUserCurrencyContext();
      const prefix =
        withSymbol && ctx.currencySymbol ? `${ctx.currencySymbol} ` : '';
      const style = resolveCurrencyStyle(ctx.country);
      const buckets = style === 'indian' ? INDIAN : INTL;

      if (value === 0) {
        return `${prefix}0`;
      }
      const abs = Math.abs(value);
      for (const [threshold, suffix] of buckets) {
        if (abs >= threshold) {
          return `${prefix}${round1(value / threshold)} ${suffix}`;
        }
      }
      return `${prefix}${round1(value)}`;
    },
  });
}
